import { encodeWav } from "./wav.js";
import { WORKLET_SOURCE } from "./worklet-source.js";
import type { RecordOptions, Recording } from "./types.js";

/**
 * Start recording from the user's microphone.
 *
 * Captures Float32 PCM at the device's native rate (typically 48 kHz),
 * downsamples to 16 kHz on the main thread, and accumulates the samples
 * in memory. Call `stop()` to retrieve the buffered audio as a
 * Float32Array, or `stopAsWav()` to retrieve a 16-bit PCM WAV Blob ready
 * for upload.
 *
 * Works in any browser with AudioWorklet support (all modern desktop +
 * mobile). Tauri webviews are just browsers — this works there too.
 *
 * Permission: the lib calls `getUserMedia({ audio: true })` itself unless
 * you provide an existing MediaStream via `opts.stream`.
 */
export async function record(opts: RecordOptions = {}): Promise<Recording> {
  if (typeof window === "undefined" || typeof AudioContext === "undefined") {
    throw new Error(
      "@unveiled/voice-engine: record() requires a browser environment with AudioContext.",
    );
  }
  const externalStream = opts.stream !== undefined;
  const stream =
    opts.stream ??
    (await navigator.mediaDevices.getUserMedia({ audio: true }));
  const ctx = new AudioContext();
  await ctx.audioWorklet.addModule(getWorkletUrl());
  const src = ctx.createMediaStreamSource(stream);
  const node = new AudioWorkletNode(ctx, "voice-engine-pcm-capture");

  const chunks: Float32Array[] = [];
  let levelRMS = 0;
  const srcRate = ctx.sampleRate;

  node.port.onmessage = (ev) => {
    const f32 = ev.data as Float32Array;
    const ds = downsampleTo16k(f32, srcRate);
    chunks.push(ds);
    let s = 0;
    for (let i = 0; i < ds.length; i++) {
      const v = ds[i] ?? 0;
      s += v * v;
    }
    levelRMS = Math.min(1, Math.sqrt(s / Math.max(1, ds.length)));
  };
  src.connect(node);
  // Intentionally NOT connecting node to ctx.destination — we don't want
  // the mic audio playing back through the speakers.

  let finalized = false;
  let finalSamples: Float32Array | null = null;

  const finalize = async (): Promise<Float32Array> => {
    if (finalized) return finalSamples ?? new Float32Array(0);
    finalized = true;
    src.disconnect();
    node.disconnect();
    if (!externalStream) {
      stream.getTracks().forEach((t) => t.stop());
    }
    await ctx.close();

    let total = 0;
    for (const c of chunks) total += c.length;
    const out = new Float32Array(total);
    let off = 0;
    for (const c of chunks) {
      out.set(c, off);
      off += c.length;
    }
    finalSamples = out;
    return out;
  };

  return {
    async stop(): Promise<Float32Array> {
      return finalize();
    },
    async stopAsWav(): Promise<Blob> {
      const samples = await finalize();
      return encodeWav(samples, 16000);
    },
    async cancel(): Promise<void> {
      await finalize();
      finalSamples = new Float32Array(0);
    },
    level: () => levelRMS,
  };
}

/**
 * Average-window downsample to 16 kHz.
 *
 * The decimation pattern in some examples (`out[i] = input[Math.floor(i*r)]`)
 * aliases. This averages each output sample over its corresponding source
 * window, which is enough fidelity for Whisper without pulling in a
 * polyphase resampler. Whisper's mel-spectrogram path is forgiving.
 */
function downsampleTo16k(input: Float32Array, srcRate: number): Float32Array {
  if (srcRate === 16000) return input;
  if (srcRate < 16000) {
    // Upsampling not supported; pass through. Whisper handles arbitrary
    // sample rates internally anyway, but the cluster expects 16k.
    return input;
  }
  const ratio = srcRate / 16000;
  const outLen = Math.floor(input.length / ratio);
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.floor((i + 1) * ratio);
    let sum = 0;
    let n = 0;
    for (let j = start; j < end && j < input.length; j++) {
      sum += input[j] ?? 0;
      n++;
    }
    out[i] = n > 0 ? sum / n : 0;
  }
  return out;
}

let workletUrl: string | null = null;
function getWorkletUrl(): string {
  if (!workletUrl) {
    const blob = new Blob([WORKLET_SOURCE], {
      type: "application/javascript",
    });
    workletUrl = URL.createObjectURL(blob);
  }
  return workletUrl;
}
